import React, { useEffect, useState } from "react";
import styles from "./CrossChargeView.less";
import { LineChartContainer } from "../../azure/common/LineChartContainer";
import { useDateRange } from "../../../hooks/useDateSelector";
import { useGetMonthlyCosmicCostByCategory } from "../../../hooks/useCosmicQuery";
import { CosmicSourceTypeEnum, ICosmicMetric } from "../../../models/CosmicModels";
import { LogComponent, LogElement } from "../../../models/LogModel";
import CostHierarchyTable from './CostHierarchyTable';
import CostAndCoreHierarchyTable from './CostAndCoreHierarchyTable';
import { Button } from "@fluentui/react-components";
import { exportStringToCsv } from "../../../utils/common";
import CosmicSelector from '../CosmicSelector';
import { useSearchParams } from "react-router-dom";
import { SearchKey } from "../../../models/SearchKey";
import moment from 'moment';
import { Dropdown } from "@fluentui/react";
import { Stack } from "@fluentui/react";


interface ICrossChargeViewProps {
    type: CosmicSourceTypeEnum;
}

interface ICrossChargeChartsProps {
    monthlyCostData: any[];
    isLoadingMonthlyCosmicCostByCategory: boolean;
    handleDownload: (title: string, metric: ICosmicMetric[]) => void;
}

const CrossChargeCharts: React.FC<ICrossChargeChartsProps> = ({
                                                                  monthlyCostData,
                                                                  isLoadingMonthlyCosmicCostByCategory,
                                                                  handleDownload
                                                              }) => {
    const getOrderedSeries = (data: any[]) => {
        if (!data || !Array.isArray(data)) return [];

        const series = data.map(item => ({
            name: item.legend,
            data: Array.isArray(item.data)
                ? item.data.map((d: { date: string; metricValue: number }) => [
                    moment(d.date).valueOf(),
                    d.metricValue ?? 0
                ])
                : []
        }));

        const allDataMap = new Map<number, number>();
        series.forEach(s => {
            s.data.forEach(([timestamp, value]: [number, number]) => {
                const currentAllValue = allDataMap.get(timestamp) ?? 0;
                allDataMap.set(timestamp, currentAllValue + value);
            });
        });

        const allSeries = {
            name: 'All',
            data: Array.from(allDataMap.entries()).sort((a, b) => a[0] - b[0])
        };

        return [allSeries, ...series];
    };

    const renderChart = (config: any, reportData: any, isLoading: boolean) => (
        <div key={config.title}>
            <div className={styles.chartWithTooltip}>
                <LineChartContainer
                    key={config.title}
                    title={config.title}
                    isLoading={isLoading}
                    isNotCurrency={config.isNotCurrency}
                    series={getOrderedSeries(reportData)}
                    logProps={{
                        logComponent: config.logComponent,
                        logElement: config.logElement,
                        customProperties: {}
                    }}
                    fillMissingAbnormalData={config.fillMissingAbnormalData}
                    suffix={config.suffix}
                    showArea={config.showArea}
                    ignoreWeekend={config.ignoreWeekend}
                    anomalies={config.anomalies}
                />
            </div>
            {(!isLoading) && (
                <Button onClick={() => handleDownload('Monthly Cost per category trend', reportData)}> Download CSV </Button>
            )}

        </div>
    );

    const chartConfig = {
        title: "Monthly Cost per Category Trend",
        isNotCurrency: false,
        fillMissingAbnormalData: true,
        logComponent: LogComponent.CosmicCrossChargeView,
        logElement: LogElement.CosmicMonthlyCostByCategory,
        showArea: true,
        ignoreWeekend: false,
        suffix: '',
        legends: [],
        dateFormatStr: "MMM",
        anomalies: [],
    };

    return (
        <>
            {renderChart(chartConfig, monthlyCostData, isLoadingMonthlyCosmicCostByCategory)}
        </>
    );
};

const CrossChargeView: React.FC<ICrossChargeViewProps> = (props) => {
    const { startDate, endDate } = useDateRange();
    const { data: monthlyCostData, isLoading: isLoadingMonthlyCosmicCostByCategory } = useGetMonthlyCosmicCostByCategory();
    const [searchParams, setSearchParams] = useSearchParams();
    const range = useDateRange();
    const [internalSelectedMonth, setInternalSelectedMonth] = useState<string>("All");

    // Generate months in range
    const getMonthsInRange = () => {
        const months: string[] = [];
        if (!range?.startDate || !range?.endDate) return months;
        const start = moment(range.startDate).startOf('month');
        const end = moment(range.endDate).endOf('month');
        const current = start.clone();
        while (current.isSameOrBefore(end)) {
            if(current.isValid()){
                months.push(current.format("YYYY-MM"));
            }
            current.add(1, 'month');
        }
        return months;
    };
    const months = getMonthsInRange();

    // Set default selected month
    React.useEffect(() => {
        if (months.length > 0) {
            setInternalSelectedMonth(months[months.length - 1]);
        }
    }, [range.startDate, range.endDate]);

    const handleMonthChange = async (event: React.FormEvent<HTMLDivElement>, option?: any) => {
        if (option) {
            setInternalSelectedMonth(option.key);
        }
    };

    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (newSearchParams.get(SearchKey.CosmicSearchFilter)) {
            newSearchParams.delete(SearchKey.CosmicSearchFilter);
        }
        const maxDateOffset = 4;
        const endDate = moment().subtract(maxDateOffset, 'day');
        const startDate = moment(endDate).subtract(5, 'months').startOf('month');
        newSearchParams.delete(SearchKey.StartDate);
        newSearchParams.delete(SearchKey.EndDate);
        newSearchParams.set(SearchKey.StartDate, startDate.format("YYYY-MM-DD"));
        newSearchParams.set(SearchKey.EndDate, endDate.format("YYYY-MM-DD"));
        setSearchParams(newSearchParams);
    }, []); // Run once on mount

    const handleDownload = (title: string, metric: ICosmicMetric[]) => {
        if (!metric) {
            return;
        }
        const csvData = prepareCSVData('Cost', metric);
        exportStringToCsv(csvData, `${title}_${startDate.format('YYYY-MM')}_${endDate.format('YYYY-MM')}.csv`);
    };

    const prepareCSVData = (metric: string, data: ICosmicMetric[]) => {
        const csvData: [number, string, number][] = [];
        const series = data.map((singleMetric: any) => ({
            name: singleMetric.legend ?? 'Unknown',
            data: singleMetric.data.map((metric: any) => [metric.date.valueOf(), metric.metricValue ?? 0])
        })) || [];

        for (const categoryName in series) {
            const data = series[categoryName].data;
            for (const item of data) {
                const cost = item[1];
                csvData.push([item[0], series[categoryName].name, cost]);
            }
        }

        // Sort by date and cost
        csvData.sort((a, b) => (new Date(a[0]).getTime() - new Date(b[0]).getTime()) || (b[2] - a[2]));

        // Format: Month, Category, Cost
        const rowData = csvData.map((item) => item.join(","));
        return ["Month", "Category", metric].join(",") + "\n" + rowData.join("\n");
    };

    return (
        <div className={styles.container}>
            <CosmicSelector comboBoxConfigs={[
                { label: "Workload", filterKey: "Workloads" },
                { label: "Namespace", filterKey: "Namespaces" },
                { label: "Region", filterKey: "Regions" },
                { label: "Ring", filterKey: "Rings" },
            ]} />

            <CrossChargeCharts
                monthlyCostData={monthlyCostData[0]?.data || []}
                isLoadingMonthlyCosmicCostByCategory={isLoadingMonthlyCosmicCostByCategory}
                handleDownload={handleDownload}
            />

            <Stack horizontal className={styles.monthSelectorRow}>
                <span className={styles.monthSelectorLabel}>Select Date</span>
                <Dropdown
                    placeholder="Select Month"
                    options={[
                        { key: "All", text: "All" },
                        ...months.map(month => ({
                            key: month,
                            text: moment(month, "YYYY-MM").format("MMMM YYYY")
                        }))
                    ]}
                    selectedKey={internalSelectedMonth}
                    onChange={handleMonthChange}
                    className={styles.monthDropdown}
                    calloutProps={{
                        styles: { root: { maxWidth: 220 } }
                    }}
                />
            </Stack>

            {internalSelectedMonth && (
                <>
                    <CostHierarchyTable selectedMonth={internalSelectedMonth} />
                    <CostAndCoreHierarchyTable selectedMonth={internalSelectedMonth} />
                </>
            )}
        </div>
    );
};

export default CrossChargeView;